/**
 * Reusable tracking hooks — fire structured journey events with
 * guaranteed once-per-mount semantics.
 */
import { useEffect, useRef, useCallback } from "react";
import analytics from "@/utils/analytics";

/**
 * useProductViewed — fires product_viewed exactly once when the
 * component mounts. Intended for product/service pages.
 */
export function useProductViewed(itemName, opts = {}) {
  const firedRef = useRef(false);
  useEffect(() => {
    if (firedRef.current || !itemName) return;
    if (analytics.productViewed(itemName, opts)) firedRef.current = true;
    // Intentionally only re-runs when itemName changes.
    // analytics (module import) and firedRef (ref) are stable.
    // Including `opts` would cause refires on every render when a
    // consumer passes an inline object — firedRef still guards but
    // we avoid the churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemName]);
}

/**
 * usePartnerViewed — fires partner_section_viewed exactly once when
 * the component mounts. Intended for partner/Work-With-Us pages.
 */
export function usePartnerViewed(itemName, opts = {}) {
  const firedRef = useRef(false);
  useEffect(() => {
    if (firedRef.current || !itemName) return;
    if (analytics.partnerSectionViewed(itemName, opts)) firedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemName]);
}

/**
 * useToolLifecycle — returns { markStarted, markCompleted } with
 * built-in once-per-session-per-tool guards. Safe to call on every
 * keystroke — only the first call per tool fires.
 *
 * Callbacks are wrapped in useCallback so consumers can safely include
 * them in effect/memo dependency arrays without triggering re-runs.
 */
export function useToolLifecycle(itemName, opts = {}) {
  const startedRef = useRef(false);
  const completedRef = useRef(false);
  const markStarted = useCallback(() => {
    if (startedRef.current || !itemName) return;
    if (analytics.toolStarted(itemName, opts)) startedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemName]);
  const markCompleted = useCallback(() => {
    // A result is only a completion when this mounted tool previously
    // recorded an eligible start. This prevents default/calculated-on-mount
    // values from becoming completions and prevents refused starts from
    // being replayed after consent changes.
    if (completedRef.current || !startedRef.current || !itemName) return;
    if (analytics.toolCompleted(itemName, opts)) completedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemName]);
  return { markStarted, markCompleted };
}

/**
 * usePartnerToolLifecycle — same pattern as useToolLifecycle but
 * fires partner_tool_started / partner_tool_completed events.
 */
export function usePartnerToolLifecycle(itemName, opts = {}) {
  const startedRef = useRef(false);
  const completedRef = useRef(false);
  const markStarted = useCallback(() => {
    if (startedRef.current || !itemName) return;
    if (analytics.partnerToolStarted(itemName, opts)) startedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemName]);
  const markCompleted = useCallback(() => {
    // Partner tools use the same real-start requirement as standard tools.
    if (completedRef.current || !startedRef.current || !itemName) return;
    if (analytics.partnerToolCompleted(itemName, opts)) completedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemName]);
  return { markStarted, markCompleted };
}
